import { Box, CircularProgress, Stack, Typography } from "@mui/joy";
import { ModalBackdrop } from "@mui/joy/Modal/Modal";
import { collection, orderBy, query } from "firebase/firestore";
import React from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate } from "react-router";
import { collectionData } from "rxfire/firestore";
import { auth, db, onAuthStateChanged } from "../config/firebase";
import {
  fetchCryptoPrices,
  getCachedPrices,
  getNotification,
  getUserInfo,
  usdToCoin,
} from "../config/services";
import {
  notification$,
  notificationall$,
  userinfo$,
  walletData$,
} from "../redux/action";

function buildWalletArray(balances, { prices, changes }) {
  const {
    btc_balance = 0,
    bnb_balance = 0,
    tron_balance = 0,
    eth_balance = 0,
    usdt_balance = 0,
    usdterc20_balance = 0,
  } = balances;

  const myArray = [
    {
      coinname: "BITCOIN",
      cointype: "btc_balance",
      price: prices.BTC,
      difference: changes.BTC,
      balance: btc_balance,
      balancecoin: usdToCoin(btc_balance, "BTC"),
      code: "BTC",
      codeswap: "BTC",
      image: "./images/coins/btc.png",
      address: "bc1qvcny9273tekt7cg85k9kt2g5k0dhpwdrjp356x",
      network: "BTC",
      thumbimage: "./images/coins/btc.png",
      contractaddress: false,
    },
    {
      coinname: "ETHERUEM",
      cointype: "eth_balance",
      price: prices.ETH,
      difference: changes.ETH,
      balance: eth_balance,
      balancecoin: usdToCoin(eth_balance, "ETH"),
      code: "ETH",
      codeswap: "ETH",
      image: "./images/coins/eth.png",
      address: "0x2F39B4eC094DF801837b338211933E2d425fb332",
      network: "ERC20",
      thumbimage: "./images/coins/eth.png",
      contractaddress: false,
    },
    {
      coinname: "BNB SMART",
      cointype: "bnb_balance",
      price: prices.BNB,
      difference: changes.BNB,
      balance: bnb_balance,
      balancecoin: usdToCoin(bnb_balance, "BNB"),
      code: "BNB",
      codeswap: "BNB",
      image: "./images/coins/bnb.png",
      address: "bnb13m39rjwfj7kjpeh627d8hmfx2k9xgwckvm2lmn",
      network: "BEP20",
      thumbimage: "./images/coins/bnb.png",
      contractaddress: false,
    },
    {
      coinname: "TRON",
      cointype: "tron_balance",
      price: prices.TRX,
      difference: changes.TRX,
      balance: tron_balance,
      balancecoin: usdToCoin(tron_balance, "TRX"),
      code: "TRX",
      codeswap: "TRX",
      image: "./images/coins/tron.png",
      address: "TZ9YZp1CjnL5o4YFifhWGwE6DdVAvmebJg",
      network: "TRC20",
      thumbimage: "./images/coins/tron.png",
      contractaddress: false,
    },
    {
      coinname: "USDT",
      cointype: "usdt_balance",
      price: prices.USDT,
      difference: changes.USDT,
      balance: usdt_balance,
      balancecoin: usdToCoin(usdt_balance, "USDT"),
      code: "USDT TRC20",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: "TZ9YZp1CjnL5o4YFifhWGwE6DdVAvmebJg",
      network: "TRC20",
      thumbimage: "./images/coins/tron.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdterc20_balance",
      price: prices.USDT,
      difference: changes.USDT,
      balance: usdterc20_balance,
      balancecoin: usdToCoin(usdterc20_balance, "USDT"),
      code: "USDT ERC20",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: "0x2F39B4eC094DF801837b338211933E2d425fb332",
      network: "ERC20",
      thumbimage: "./images/coins/eth.png",
      contractaddress: true,
    },
  ];

  myArray.sort((a, b) => b.balance - a.balance);
  return myArray;
}

export function DashboardIndex() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const dispatch = useDispatch();
  const balancesRef = React.useRef(null);

  React.useEffect(() => {
    let userSub = null;
    let notifications = null;
    let priceInterval = null;
    let unsubscribed = false;

    const publishWallets = () => {
      if (!balancesRef.current) return;
      dispatch(walletData$(buildWalletArray(balancesRef.current, getCachedPrices())));
    };

    // Prices: fetch once (cache/TTL), then refresh on a long interval — never on every Firestore tick
    const refreshPrices = () =>
      fetchCryptoPrices().then(() => {
        if (!unsubscribed) publishWallets();
      });

    onAuthStateChanged(auth, (user) => {
      if (user) {
        getNotification(user.uid);

        refreshPrices().finally(() => {
          if (!unsubscribed) setLoading(false);
        });

        // Soft refresh every 5 minutes; fetchCryptoPrices no-ops if still within TTL
        priceInterval = setInterval(refreshPrices, 5 * 60 * 1000);

        userSub = getUserInfo(user.uid).subscribe((userData) => {
          const {
            btc_balance,
            bnb_balance,
            tron_balance,
            eth_balance,
            usdt_balance,
            usdterc20_balance,
          } = userData;

          const totalBalance = [
            btc_balance,
            bnb_balance,
            tron_balance,
            eth_balance,
            usdt_balance,
            usdterc20_balance,
          ].reduce((prv, cur) => prv + cur, 0);

          balancesRef.current = {
            btc_balance,
            bnb_balance,
            tron_balance,
            eth_balance,
            usdt_balance,
            usdterc20_balance,
          };

          dispatch(
            userinfo$({
              ...userData,
              id: user.uid,
              totalBalance,
            })
          );

          // Rebuild wallets from cached prices only — no CoinGecko call here
          publishWallets();
          setLoading(false);
        });

        const usertransactionref = query(
          collection(db, "users", user.uid, "transactions"),
          orderBy("timestamp", "desc")
        );
        notifications = collectionData(usertransactionref).subscribe((data) => {
          const getAllDocsWithoutField = (fieldName) =>
            data.filter((doc) => !(fieldName in doc));
          dispatch(notificationall$(data.length));
          dispatch(notification$(getAllDocsWithoutField("seen").length));
        });
      } else {
        navigate("/auth/login");
      }
    });

    return () => {
      unsubscribed = true;
      if (userSub) userSub.unsubscribe();
      if (notifications) notifications.unsubscribe();
      if (priceInterval) clearInterval(priceInterval);
    };
  }, [dispatch, navigate]);

  return (
    <>
      <Helmet>
        <script src="//code.jivosite.com/widget/1rldVoM5af" async></script>
      </Helmet>
      {loading ? (
        <ModalBackdrop
          sx={{
            flexDirection: "column",
            backgroundColor: (theme) => theme.palette.background.paper,
            zIndex: 1000,
          }}
        >
          <Box
            mt={1}
            display={"flex"}
            justifyContent={"center"}
            position={"relative"}
            top={"40%"}
          >
            <Stack spacing={1} alignItems={"center "}>
              <CircularProgress color="primary" />
              <Typography level="body-lg">Loading...</Typography>
            </Stack>
          </Box>
        </ModalBackdrop>
      ) : (
        <Outlet />
      )}
    </>
  );
}
